import { Dispatch } from "react";
import axios from "axios";
import jwtAxios from "../../auth/jwtAxios";
import { AppActions } from "../../types";
import { KnownRole } from "../../types/models/KnownRole";
import { MyLocation, MyRole, UserRole } from "../../types/models/MyRole";
import {
  GET_KNOWN_ROLES,
  GET_ROLE_BUNDLE_MAPPINGS,
  GET_MY_ROLES,
  GET_ROLE_BUNDLES,
  GET_SKILL_DATA,
} from "../../types/actions/Role.actions";
import {
  PostUpdateLocationSubscriptionRequest,
  PostUpdateSubscriptionsRequest,
} from "../../types/models/PostUpdateSubscriptionsRequest";
import { fetchStart, fetchSuccess, fetchError } from "./Common";
import { getDisplayErrorMessage } from "../../utils/StringUtils";
import { KnownLocation } from "../../types/models/KnownLocation";
import { RoleBundleRequestBody } from "../../types/models/RoleBundleRequestBody";
import { RoleBundle } from "../../types/models/RoleBundle";
import { RoleBundleMapping } from "../../types/models/LTOSetup";
import { RoleBundleMappingRequestBody } from "../../types/models/LTOSetupRequestBody";
import { SkillData } from "../../types/models/SkillData";
import { MyRoleBundle } from "../../types/models/MyRoleBundle";

export const getKnownRoles = (callBack: (knownRoles: KnownRole[] | undefined) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get("api/KnownRoles")
      .then((data) => {
        if (data.status === 200) {
          dispatch(fetchSuccess());
          const roles: KnownRole[] = data.data.Values;
          const locations: KnownLocation[] = data.data.Locations;
          const knownValues = { roles: roles.filter((role) => role.Active), locations: locations };
          dispatch({ type: GET_KNOWN_ROLES, payload: knownValues });
          callBack(roles);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack(undefined);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack(undefined);
      });
  };
};

export const getMyRoles = (callBack: (result: UserRole | undefined) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get("api/MyRoles")
      .then((data) => {
        if (data.status === 200) {
          dispatch(fetchSuccess());
          const roles: MyRole[] = data.data.Values;
          const locations: MyLocation[] = data.data.Locations;
          const values = {
            roles: roles,
            locations: locations,
            contentAuthor: data.data.ContentAuthor,
            onboarded: data.data.Onboarded,
          };
          dispatch({ type: GET_MY_ROLES, payload: values });
          callBack(values);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack(undefined);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack(undefined);
      });
  };
};

export const postUpdateRoleSubscription = (
  request: PostUpdateSubscriptionsRequest,
  callBack?: (result: boolean) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("api/UpdateSubscriptions", request)
      .then((data) => {
        if (data.status === 200) {
          dispatch(fetchSuccess());
          if (callBack) callBack(true);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          if (callBack) callBack(false);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        if (callBack) callBack(false);
      });
  };
};

export const postUpdateLocationSubscription = (
  request: PostUpdateLocationSubscriptionRequest,
  callBack?: (result: boolean) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("api/UpdateLocations", request)
      .then((data) => {
        if (data.status === 200) {
          dispatch(fetchSuccess());
          if (callBack) {
            callBack(true);
          }
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status: " + data.status));
        }
      })
      .catch(() => {
        if (callBack) {
          callBack(false);
        }
      });
  };
};

export const getRoleBundles = (callBack: (bundles: RoleBundle[]) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("api/RoleBundles")
      .then((data) => {
        if (data.status === 200) {
          const roleBundles: RoleBundle[] = data.data.Values ?? [];
          const sortedRoleBundles = roleBundles.sort((a, b) =>
            a.DisplayName.localeCompare(b.DisplayName)
          );
          dispatch({ type: GET_ROLE_BUNDLES, payload: sortedRoleBundles });
          callBack(sortedRoleBundles);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack([]);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack([]);
      });
  };
};

export const createRoleBundle = (
  request: RoleBundleRequestBody,
  callBack?: (roleBundle: RoleBundle | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("api/RoleBundles", request)
      .then((data) => {
        if (data.status === 200 || data.status === 201) {
          dispatch(fetchSuccess());
          if (callBack) {
            callBack(data.data);
          }
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status: " + data.status));
        }
      })
      .catch(() => {
        if (callBack) {
          callBack(undefined);
        }
      });
  };
};

export const updateRoleBundle = (
  roleBundleId: string,
  request: RoleBundleRequestBody,
  callBack?: (result: boolean) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .put("api/RoleBundles/" + roleBundleId, request)
      .then((data) => {
        if (data.status === 200 || data.status === 204) {
          dispatch(fetchSuccess());
          if (callBack) {
            callBack(true);
          }
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status: " + data.status));
        }
      })
      .catch(() => {
        if (callBack) {
          callBack(false);
        }
      });
  };
};

export const deleteRoleBundle = (roleBundleId: string, callBack?: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .delete("api/RoleBundles/" + roleBundleId)
      .then((data) => {
        if (data.status === 200 || data.status === 204) {
          dispatch(fetchSuccess());
          if (callBack) {
            callBack(true);
          }
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status: " + data.status));
        }
      })
      .catch(() => {
        if (callBack) {
          callBack(false);
        }
      });
  };
};

export const updateOnboardedFlag = () => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios.post("api/Onboarded").catch(() => {
      dispatch(fetchError("Something went wrong, Please try again!"));
    });
  };
};

export const getSkillData = (callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("api/RoleBundleMappings/KleSkills")
      .then((data) => {
        if (data.status === 200) {
          const skills: SkillData[] = data.data.Values;
          const sortedSkills = skills.sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
          dispatch({ type: GET_SKILL_DATA, payload: sortedSkills });
          callBack(true);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack(false);
      });
  };
};

export const getRoleBundleMappings = (callBack: (result: boolean) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("api/RoleBundleMappings")
      .then((data) => {
        if (data.status === 200) {
          // sorting by name
          const mappings: RoleBundleMapping[] = data.data.Values;
          const sortedMappings = mappings.sort((a, b) => a.Name.localeCompare(b.Name));
          dispatch({ type: GET_ROLE_BUNDLE_MAPPINGS, payload: sortedMappings });
          callBack(true);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack(false);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack(false);
      });
  };
};

export const createRoleBundleMapping = (
  request: RoleBundleMappingRequestBody,
  callBack?: (mapping: RoleBundleMapping | undefined) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .post("api/RoleBundleMappings", request)
      .then((data) => {
        if (data.status === 200 || data.status === 201) {
          dispatch(fetchSuccess());
          if (callBack) {
            callBack(data.data);
          }
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status: " + data.status));
        }
      })
      .catch(() => {
        if (callBack) {
          callBack(undefined);
        }
      });
  };
};

export const updateRoleBundleMapping = (
  mappingId: string,
  request: RoleBundleMappingRequestBody,
  callBack?: (result: boolean) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .put("api/RoleBundleMappings/" + mappingId, request)
      .then((data) => {
        if (data.status === 200 || data.status === 204) {
          dispatch(fetchSuccess());
          if (callBack) {
            callBack(true);
          }
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status: " + data.status));
        }
      })
      .catch(() => {
        if (callBack) {
          callBack(false);
        }
      });
  };
};

export const deleteRoleBundleMapping = (
  mappingId: string,
  callBack?: (result: boolean) => void
) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .delete("api/RoleBundleMappings/" + mappingId)
      .then((data) => {
        if (data.status === 200 || data.status === 204) {
          dispatch(fetchSuccess());
          if (callBack) {
            callBack(true);
          }
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status: " + data.status));
        }
      })
      .catch(() => {
        if (callBack) {
          callBack(false);
        }
      });
  };
};

export const getMyRoleBundles = (callBack: (bundles: MyRoleBundle[] | undefined) => void) => {
  return (dispatch: Dispatch<AppActions>) => {
    jwtAxios
      .get("api/MyRoleBundles")
      .then((data) => {
        if (data.status === 200) {
          callBack(data.data.Values);
        } else {
          dispatch(fetchError("Something went wrong, Please try again! Status:" + data.status));
          callBack(undefined);
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error) || error instanceof Error)
          dispatch(fetchError(getDisplayErrorMessage(error)));
        callBack(undefined);
      });
  };
};
